/**
 * AS REGRAS DE FORMATO DE E-MAIL E DE FORCA DE SENHA, para a PLATAFORMA INTEIRA.
 *
 * Pedido do fundador em 07/09/2026: "PADRAO DA PLATAFORMA TODA (VEJA ONDE VAI COLOCAR ISSO
 * PARA NAO ACONTECER O MESMO PROBLEMA, EMAIL TEM QUE TER FORMATO MINIMO ALFA@ALFA - SENHA
 * NO MINIMO 8 DIGITOS NUMERO+ALFA+CARACTER".
 *
 * O DEFEITO QUE COMPROU ESTE ARQUIVO, medido na tela dele no mesmo dia: o campo de e-mail
 * do login aceitou a letra `A` sozinha e o formulario seguiu, reclamando so da senha. A
 * validacao que existia checava APENAS se o campo estava vazio.
 *
 * ------------------------------------------------------------------------------------
 * POR QUE ESTE MODULO MORA EM `domain/core` E NAO EM `packages/auth`
 * ------------------------------------------------------------------------------------
 * Porque a mesma regra precisa valer nos DOIS lados, e a fronteira de camadas do eslint
 * so deixa um lugar cumprir isso: `packages/auth` e camada 1, que e FRONT, e servico de
 * backend nao importa camada 1 (eslint.config.js:526). Escrita la, a regra ficaria so no
 * navegador, e o cadastro feito por rota HTTP entraria com senha fraca sem ninguem ver.
 *
 * E ele mora num SUBPATH PROPRIO (`@complexity/domain-core/credenciais`), e nao no index,
 * porque o index reexporta `./sessao`, que importa NestJS. Um app de navegador que
 * importasse o index arrastaria o Nest inteiro para o bundle.
 *
 * ESTE ARQUIVO NAO IMPORTA NADA. E regra de negocio pura, sem banco, sem HTTP, sem relogio,
 * e e isso que permite exercita-la em milissegundos com o caso de borda escrito a mao.
 */
/**
 * O MINIMO DE CARACTERES DA SENHA, e o numero e dele: "NO MINIMO 8 DIGITOS".
 *
 * Fica como constante exportada porque a tela precisa ESCREVER o numero na mensagem de
 * ajuda, e um 8 digitado na mensagem envelheceria sozinho no dia em que a regra mudasse.
 */
export const SENHA_TAMANHO_MINIMO = 8;
/**
 * O QUE CONTA COMO CARACTERE ESPECIAL, declarado por exclusao e nao por lista.
 *
 * Lista fechada (`!@#$%`) recusaria senha legitima com um caractere que ninguem lembrou de
 * incluir, e a pessoa nao teria como adivinhar qual. Aqui e especial tudo que NAO e letra,
 * NAO e digito e NAO e espaco: assim `~`, `|`, `¬` e `£` entram sem precisar de manutencao.
 *
 * O ESPACO FICA DE FORA de proposito: ele e invisivel, entra por engano ao colar, e uma
 * senha que so passa por causa de um espaco no fim e uma senha que a pessoa nao consegue
 * digitar de novo.
 */
const EH_ESPECIAL = (c) => !/[a-zA-Z0-9]/.test(c) && !/\s/.test(c);
/**
 * O FORMATO MINIMO DE E-MAIL, e "minimo" e a palavra dele: `alfa@arroba`.
 *
 * POR QUE NAO SE EXIGE DOMINIO DE TOPO (`.com`), e isto NAO e descuido: na plataforma, a conta de trabalho
 * da plataforma e `cds@cds`, sem ponto e sem TLD, por decisao registrada no CLAUDE geral. Uma
 * regra que exigisse `.algo` recusaria a propria conta com que este sistema e conferido, e o
 * sintoma acusaria a CONTA quando o problema seria a REGRA.
 *
 * O que a expressao exige, e nada alem disso:
 *   · pelo menos um caractere antes do arroba, e nenhum deles e arroba ou espaco;
 *   · exatamente UM arroba, porque dois nunca formam endereco valido;
 *   · pelo menos um caractere depois, e nenhum deles e arroba ou espaco.
 *
 * VALIDAR E-MAIL POR EXPRESSAO NUNCA PROVA QUE ELE EXISTE, e nenhuma expressao provaria: o
 * unico teste real e enviar uma mensagem e ela chegar. O que esta regra impede e o erro de
 * digitacao obvio, que e o caso que aconteceu na tela dele.
 */
const FORMATO_EMAIL = /^[^@\s]+@[^@\s]+$/;
/**
 * Valida o E-MAIL.
 *
 * A comparacao NAO normaliza caixa nem acentos, e apenas apara espaco nas pontas: espaco
 * colado junto e o erro mais comum de quem copia de outro lugar, e ele nao e escolha de
 * ninguem. Alterar a caixa, ao contrario, mudaria o valor que a pessoa digitou.
 */
export function validarEmail(valor) {
    const limpo = valor.trim();
    // Vazio tem motivo PROPRIO, e nao vira "formato invalido": a pessoa que nao digitou nada
    // precisa ler "informe o e-mail", e nao uma aula sobre o formato que ela nao tentou usar.
    if (limpo.length === 0) {
        return { ok: false, motivo: 'vazio', mensagem: 'Informe o e-mail.' };
    }
    // A mensagem MOSTRA o formato esperado, porque "e-mail invalido" nao ensina o que corrigir.
    if (!FORMATO_EMAIL.test(limpo)) {
        return {
            ok: false,
            motivo: 'formato-de-email',
            mensagem: 'O e-mail precisa ter o formato nome@dominio.',
        };
    }
    return { ok: true };
}
/**
 * Valida a SENHA: no minimo 8 caracteres, com numero, letra e caractere especial.
 *
 * A ORDEM DAS CONFERENCIAS E DELIBERADA, e ela e do mais grosso para o mais fino: tamanho
 * primeiro, depois cada familia de caractere. Quem digitou tres letras precisa ler que
 * faltam caracteres, e nao que falta um simbolo numa senha que seria curta de qualquer jeito.
 *
 * SO O PRIMEIRO PROBLEMA E RELATADO, de proposito. Despejar as quatro exigencias de uma vez
 * transforma a mensagem numa parede que ninguem le, e a pessoa corrige uma por vez de todo
 * jeito.
 */
export function validarSenha(valor) {
    // A senha NAO leva `trim`: espaco pode ser parte legitima dela, e apara-lo mudaria em
    // silencio o valor que a pessoa escolheu. Isto e o oposto da decisao do e-mail, e a
    // diferenca e que ninguem escolhe um e-mail com espaco na ponta.
    if (valor.length === 0) {
        return { ok: false, motivo: 'vazio', mensagem: 'Informe a senha.' };
    }
    if (valor.length < SENHA_TAMANHO_MINIMO) {
        return {
            ok: false,
            motivo: 'senha-curta',
            mensagem: `A senha precisa ter pelo menos ${SENHA_TAMANHO_MINIMO} caracteres.`,
        };
    }
    if (!/[0-9]/.test(valor)) {
        return {
            ok: false,
            motivo: 'senha-sem-numero',
            mensagem: 'A senha precisa ter pelo menos um número.',
        };
    }
    if (!/[a-zA-Z]/.test(valor)) {
        return {
            ok: false,
            motivo: 'senha-sem-letra',
            mensagem: 'A senha precisa ter pelo menos uma letra.',
        };
    }
    // `Array.from` e nao `split('')`: split quebra emoji e acentuacao composta ao meio, e cada
    // metade seria contada como um caractere que nao existe.
    if (!Array.from(valor).some(EH_ESPECIAL)) {
        return {
            ok: false,
            motivo: 'senha-sem-especial',
            mensagem: 'A senha precisa ter pelo menos um caractere especial, como @ ou #.',
        };
    }
    return { ok: true };
}
/**
 * A frase que a tela mostra ANTES de a pessoa errar, junto do campo.
 *
 * Ela existe porque exigencia que so aparece depois do erro faz a pessoa tentar, falhar e
 * tentar de novo. Dizer antes custa uma linha e economiza duas tentativas.
 */
export const AJUDA_SENHA = `Pelo menos ${SENHA_TAMANHO_MINIMO} caracteres, com número, letra e um caractere especial.`;
//# sourceMappingURL=credenciais.js.map